/**
 * views/WarehouseManagement.tsx
 * ─────────────────────────────
 * Admin UI for creating and managing Warehouses.
 * Uses shadcn Table + Dialog instead of raw HTML.
 */

import React, { useRef, useState } from 'react';
import { PlusCircle, Pencil, Power, Trash2, Building2, Upload } from 'lucide-react';

import { useAuth }      from '@/contexts/AuthContext';
import { useWarehouse } from '@/contexts/WarehouseContext';
import { apiClient, ApiError } from '@/api/client';
import type { WarehouseItem } from '@/types';

import { Button }   from '@/components/ui/button';
import { Badge }    from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';

// ── Create-warehouse form (inside Dialog) ─────────────────────────────────────

interface CreateFormState {
  name: string; code: string; location: string;
}

function autoCode(name: string) {
  return name.toUpperCase().replace(/[^A-Z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function CreateWarehouseDialog({ onCreated }: { onCreated: () => void }) {
  const [open,  setOpen]  = useState(false);
  const [form,  setForm]  = useState<CreateFormState>({ name: '', code: '', location: '' });
  const [locFile, setLocFile] = useState<File | null>(null);
  const locRef = useRef<HTMLInputElement>(null);
  const [busy,    setBusy]    = useState(false);
  const [error,   setError]   = useState<string | null>(null);
  const [dupWarn, setDupWarn] = useState<{ count: number; codes: string[] } | null>(null);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setForm(f => ({ ...f, name, code: f.code === autoCode(f.name) ? autoCode(name) : f.code }));
  };

  const reset = () => {
    setForm({ name: '', code: '', location: '' });
    setLocFile(null);
    if (locRef.current) locRef.current.value = '';
    setError(null);
    setDupWarn(null);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null); setDupWarn(null);
    try {
      const fd = new FormData();
      fd.append('name', form.name.trim());
      fd.append('code', form.code.trim().toUpperCase());
      if (form.location.trim()) fd.append('location', form.location.trim());
      if (locFile) fd.append('locations_file', locFile);
      const created = await apiClient.postForm<WarehouseItem>('/api/manage/warehouses', fd);
      if ((created.duplicate_count ?? 0) > 0) {
        setDupWarn({ count: created.duplicate_count!, codes: created.duplicate_codes ?? [] });
      }
      onCreated();
      if (!dupWarn) { setOpen(false); reset(); }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create warehouse.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button size="sm" className="gap-1.5">
          <PlusCircle className="h-4 w-4" />
          New Warehouse
        </Button>
      </DialogTrigger>

      <DialogContent className="max-w-xl bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Add New Warehouse
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">Warehouse Name *</label>
              <input
                className="form-input w-full"
                value={form.name}
                onChange={handleNameChange}
                placeholder="e.g. DHL Fonterra Singapore"
                required disabled={busy}
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">
                Code *
                <span className="ml-1 font-normal text-muted-foreground">(unique)</span>
              </label>
              <input
                className="form-input w-full"
                value={form.code}
                onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                placeholder="DHL-FONTERRA-SG"
                required disabled={busy}
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium">
              Location / Address
              <span className="ml-1 font-normal text-muted-foreground">(optional)</span>
            </label>
            <input
              className="form-input w-full"
              value={form.location}
              onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
              placeholder="12 Harbour Drive, Singapore 618923"
              disabled={busy}
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium">
              Location Master File
              <span className="ml-1 font-normal text-muted-foreground">
                — optional; CSV/Excel, first column = location codes
              </span>
            </label>
            <input
              ref={locRef}
              className="form-input w-full"
              type="file"
              accept=".xlsx,.xls,.xlsm,.csv"
              onChange={e => setLocFile(e.target.files?.[0] ?? null)}
              disabled={busy}
            />
            {locFile && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {locFile.name} ({(locFile.size / 1024).toFixed(0)} KB)
              </p>
            )}
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          {dupWarn && (
            <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              <strong>{dupWarn.count}</strong> duplicate code{dupWarn.count !== 1 ? 's' : ''} merged.
              {dupWarn.codes.length > 0 && (
                <details className="mt-1">
                  <summary className="cursor-pointer">Show ({dupWarn.codes.length})</summary>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {dupWarn.codes.map(c => (
                      <code key={c} className="rounded bg-amber-100 px-1 text-xs">{c}</code>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy || !form.name.trim() || !form.code.trim()}
            >
              {busy ? 'Creating…' : 'Create Warehouse'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Upload-locations dialog ───────────────────────────────────────────────────

interface LocationUploadResult {
  location_count: number;
  aisles: string[];
  rejected_count: number;
  duplicate_count: number;
  duplicate_codes: string[];
}

function UploadLocationsDialog({
  warehouse,
  onUploaded,
}: {
  warehouse: WarehouseItem;
  onUploaded: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<LocationUploadResult | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setFile(null);
    setError(null);
    setResult(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) { setError('Please select a file.'); return; }
    setBusy(true); setError(null);
    try {
      const fd = new FormData();
      fd.append('locations_file', file);
      const res = await apiClient.postForm<LocationUploadResult>(
        `/api/manage/warehouses/${warehouse.id}/locations`,
        fd,
      );
      setResult(res);
      onUploaded();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Upload failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="h-7 gap-1 px-2 text-xs">
          <Upload className="h-3 w-3" />
          {warehouse.location_count > 0 ? 'Re-upload' : 'Upload Locs'}
        </Button>
      </DialogTrigger>

      <DialogContent className="max-w-md bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-4 w-4" />
            Upload Locations
            <span className="truncate text-muted-foreground font-normal">— {warehouse.name}</span>
          </DialogTitle>
        </DialogHeader>

        {result ? (
          <div className="space-y-4 mt-2">
            <div className="rounded-lg bg-green-50 border border-green-200 p-4 space-y-2">
              <p className="font-semibold text-green-800 text-sm">
                ✓ {result.location_count.toLocaleString()} locations ingested
                {result.aisles.length > 0 && (
                  <span className="font-normal"> across {result.aisles.length} aisle{result.aisles.length !== 1 ? 's' : ''}</span>
                )}
              </p>
              {result.rejected_count > 0 && (
                <p className="text-sm text-amber-700">
                  ⚠ {result.rejected_count} row{result.rejected_count !== 1 ? 's' : ''} skipped (malformed or empty)
                </p>
              )}
              {result.duplicate_count > 0 && (
                <p className="text-sm text-amber-700">
                  ⚠ {result.duplicate_count} duplicate code{result.duplicate_count !== 1 ? 's' : ''} merged
                </p>
              )}
              {result.aisles.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-1">
                  {result.aisles.map(a => (
                    <code key={a} className="rounded bg-green-100 px-1.5 py-0.5 text-xs font-mono">{a}</code>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button onClick={() => { setOpen(false); reset(); }}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 mt-2">
            <p className="text-sm text-muted-foreground">
              Upload a CSV or XLSX file with location codes in the first column.
              All existing locations for <strong>{warehouse.name}</strong> will be replaced.
            </p>
            <div className="space-y-1">
              <label className="text-sm font-medium">
                Locations file *
                <span className="ml-1 font-normal text-muted-foreground">CSV / XLSX / XLS</span>
              </label>
              <input
                ref={fileRef}
                className="form-input w-full"
                type="file"
                accept=".xlsx,.xls,.xlsm,.csv"
                onChange={e => setFile(e.target.files?.[0] ?? null)}
                disabled={busy}
              />
              {file && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  {file.name} ({(file.size / 1024).toFixed(0)} KB)
                </p>
              )}
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={busy}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy || !file}>
                {busy ? 'Uploading…' : 'Upload'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Edit-warehouse dialog ─────────────────────────────────────────────────────

function EditWarehouseDialog({
  warehouse,
  onSaved,
}: {
  warehouse: WarehouseItem;
  onSaved: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(warehouse.name);
  const [location, setLocation] = useState(warehouse.location ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true); setError(null);
    try {
      await apiClient.patch(`/api/manage/warehouses/${warehouse.id}`, {
        name: name.trim(),
        location: location.trim() || null,
      });
      onSaved();
      setOpen(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to save.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => { setOpen(v); if (!v) { setName(warehouse.name); setLocation(warehouse.location ?? ''); setError(null); } }}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="h-7 gap-1 px-2 text-xs">
          <Pencil className="h-3 w-3" /> Edit
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md bg-white">
        <DialogHeader>
          <DialogTitle>Edit Warehouse</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="space-y-1">
            <label className="text-sm font-medium">Name</label>
            <input
              className="form-input w-full"
              value={name}
              onChange={e => setName(e.target.value)}
              disabled={busy}
              autoFocus
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Location / Address</label>
            <input
              className="form-input w-full"
              value={location}
              onChange={e => setLocation(e.target.value)}
              placeholder="optional"
              disabled={busy}
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy || !name.trim()}>{busy ? 'Saving…' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function WarehouseManagement() {
  const { user } = useAuth();
  const { warehouses, refreshWarehouses } = useWarehouse();
  const canDelete = user?.role === 'manager' || user?.role === 'admin' || user?.role === 'super_admin';

  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [deletingId,  setDeletingId]  = useState<string | null>(null);

  const toggleActive = async (w: WarehouseItem) => {
    setTogglingId(w.id);
    try {
      await apiClient.patch(`/api/manage/warehouses/${w.id}`, { is_active: !w.is_active });
      await refreshWarehouses();
    } catch { /* silently ignore */ } finally {
      setTogglingId(null);
    }
  };

  const permanentlyDelete = async (w: WarehouseItem) => {
    const msg =
      `PERMANENTLY DELETE "${w.name}" (${w.code})?\n\n` +
      `This removes ALL data: ${w.location_count.toLocaleString()} locations, ` +
      `all SOH snapshots, SKU rules, missions, and scan results.\n\nThis cannot be undone.`;
    if (!confirm(msg)) return;
    setDeletingId(w.id);
    try {
      await apiClient.delete(`/api/manage/warehouses/${w.id}`);
      await refreshWarehouses();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : 'Failed to delete warehouse.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">
            {warehouses.length} warehouse{warehouses.length !== 1 ? 's' : ''} registered
          </p>
        </div>
        <CreateWarehouseDialog onCreated={refreshWarehouses} />
      </div>

      {/* Table */}
      {warehouses.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-12 text-center">
          <Building2 className="mx-auto mb-3 h-10 w-10 text-muted-foreground/50" />
          <p className="text-sm font-medium">No warehouses yet</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Click "New Warehouse" to add your first site.
          </p>
        </div>
      ) : (
        <div className="rounded-lg border bg-white">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Coverage</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {warehouses.map(w => (
                <TableRow key={w.id} className={w.is_active ? '' : 'opacity-60'}>
                  <TableCell className="font-medium">{w.name}</TableCell>
                  <TableCell>
                    <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{w.code}</code>
                  </TableCell>
                  <TableCell className="max-w-[180px] truncate text-muted-foreground">
                    {w.location ?? <span className="italic">—</span>}
                  </TableCell>
                  <TableCell>
                    {w.location_count > 0 ? (
                      <span className="text-xs text-muted-foreground">
                        {w.location_count.toLocaleString()} locs
                        {w.aisles.length > 0 && ` · ${w.aisles.length} aisles`}
                      </span>
                    ) : (
                      <Badge variant="warning" className="text-xs">No locations</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={w.is_active ? 'success' : 'muted'}>
                      {w.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <UploadLocationsDialog warehouse={w} onUploaded={refreshWarehouses} />
                      <EditWarehouseDialog warehouse={w} onSaved={refreshWarehouses} />

                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 gap-1 px-2 text-xs"
                        onClick={() => toggleActive(w)}
                        disabled={togglingId === w.id}
                        title={w.is_active ? 'Disable warehouse' : 'Enable warehouse'}
                      >
                        <Power className="h-3 w-3" />
                        {togglingId === w.id ? '…' : w.is_active ? 'Disable' : 'Enable'}
                      </Button>

                      {!w.is_active && canDelete && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 gap-1 px-2 text-xs text-destructive hover:text-destructive"
                          onClick={() => permanentlyDelete(w)}
                          disabled={deletingId === w.id}
                          title="Permanently delete warehouse and all data"
                        >
                          <Trash2 className="h-3 w-3" />
                          {deletingId === w.id ? '…' : 'Delete'}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
